/**
 * BudgetPage — saldo fantamilioni + storico movimenti budget personali.
 *
 * Vista per ogni partecipante (selettore squadra). Mostra il saldo budget
 * fantamilioni della stagione attiva + lista completa movimenti budget
 * (entrate quote, spese mercato, multe, ecc).
 *
 * Auto-seleziona la propria squadra se utente è presidente di una.
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { type MovimentoBudget, useBudgetSaldo } from "../../hooks/useFase2";
import { useStagioneCorrente } from "../../hooks/useStagione";
import { useMieSquadre } from "../../hooks/useFase7";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { SquadraSelector } from "../common/SquadraSelector";
import { tr } from "../../i18n";

const TIPO_LABEL: Record<string, string> = {
  iscrizione_iniziale: tr("Dotazione iniziale"),
  iscrizione_stagionale: tr("Quota stagionale"),
  iscrizione_invernale: tr("Quota invernale"),
  ribasso: tr("Ribasso"),
  rinnovo: tr("Rinnovo"),
  abbandono: tr("Abbandono SA"),
  vendita: tr("Vendita"),
  acquisto: tr("Acquisto"),
  scambio: tr("Scambio"),
  premio: tr("Premio"),
  multa: tr("Multa"),
  storno: tr("Storno"),
  altro: tr("Altro"),
};

// Andrea 2026-10-16: i movimenti si leggono per famiglia (asta, mercato,
// scommesse, premi...), con totale e numero, cosi' si controlla ogni squadra.
const GRUPPI: Array<{ id: string; titolo: string; tipi: string[] }> = [
  { id: "asta", titolo: tr("Acquisti all'asta"), tipi: ["acquisto_svincolato_asta"] },
  {
    id: "mercato",
    titolo: tr("Mercato (acquisti, vendite, scambi)"),
    tipi: [
      "acquisto",
      "vendita",
      "conguaglio_scambio",
      "scambio",
      "svincolo_admin",
      "rettifica_prezzo",
    ],
  },
  { id: "rinnovi", titolo: tr("Rinnovi"), tipi: ["rinnovo"] },
  {
    id: "scommesse",
    titolo: tr("Scommesse"),
    tipi: ["scommessa_puntata", "scommessa_vincita", "scommessa_rimborso"],
  },
  {
    id: "premi",
    titolo: tr("Dotazioni e premi"),
    tipi: [
      "iniziale_500",
      "stagionale_150",
      "invernale_50",
      "iscrizione_iniziale",
      "iscrizione_stagionale",
      "iscrizione_invernale",
      "piazzamento_campionato",
      "piazzamento_cl",
      "piazzamento_supercoppa",
      "premio",
      "compensazione_abbandono_serie_a",
      "rimborso_rinnovo_post_abbandono",
      "abbandono",
    ],
  },
  { id: "sanzioni", titolo: tr("Multe e penali"), tipi: ["multa", "penale_cambio_nome"] },
];
const GRUPPO_ALTRO = { id: "altro", titolo: tr("Altro"), tipi: [] as string[] };

function gruppoDi(tipo: string) {
  return GRUPPI.find((g) => g.tipi.includes(tipo)) ?? GRUPPO_ALTRO;
}

function Importo({ valore, className = "" }: { valore: number; className?: string }) {
  return (
    <span
      className={`font-mono font-bold ${valore >= 0 ? "text-success" : "text-danger"} ${className}`}
    >
      {valore >= 0 ? "+" : ""}
      {valore.toFixed(2)}M
    </span>
  );
}

function RigaMovimento({ m }: { m: MovimentoBudget }) {
  return (
    <div className="flex items-center gap-3 p-3 border-b border-primary/10 last:border-b-0 text-sm">
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{m.descrizione}</div>
        <div className="text-[10px] text-fg-muted font-mono mt-0.5">
          {m.data_movimento} · {TIPO_LABEL[m.tipo] ?? etichetta(m.tipo)}
          {m.storna_movimento_id && <span className="ml-2 text-danger">{tr("↺ storno")}</span>}
        </div>
      </div>
      <Importo valore={Number(m.importo_milioni)} />
    </div>
  );
}

export function BudgetPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: stagioneAttiva } = useStagioneCorrente();
  const stagioneId = stagioneAttiva?.id ?? null;
  // Andrea 2026-09-01: anche l'allenatore in seconda vede il budget della
  // squadra che gestisce (useMieSquadre unisce presidenza + gestite).
  const { data: mieSquadre } = useMieSquadre(user?.id);
  const miaSquadra = mieSquadre[0];
  const [squadraId, setSquadraId] = useState<string>(miaSquadra?.id ?? "");

  const { data: budget, isLoading } = useBudgetSaldo(stagioneId, squadraId || null);
  // "gruppi" = per famiglia (default), "cronologia" = tutto in ordine di data
  const [vista, setVista] = useState<"gruppi" | "cronologia">("gruppi");
  const [chiusi, setChiusi] = useState<Set<string>>(new Set());
  const gruppi = useMemo(() => {
    const movimenti = budget?.ultimi_movimenti ?? [];
    const per = new Map<string, { titolo: string; righe: typeof movimenti; totale: number }>();
    for (const g of [...GRUPPI, GRUPPO_ALTRO])
      per.set(g.id, { titolo: g.titolo, righe: [], totale: 0 });
    for (const m of movimenti) {
      const g = gruppoDi(m.tipo);
      const voce = per.get(g.id)!;
      voce.righe.push(m);
      voce.totale += Number(m.importo_milioni);
    }
    return [...per.entries()]
      .filter(([, v]) => v.righe.length > 0)
      .map(([id, v]) => ({ id, ...v }));
  }, [budget]);
  const toggle = (id: string) =>
    setChiusi((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Admin: ricostruzione one-shot delle descrizioni movimenti operazioni
  // mercato (Andrea 2026-05-19: i vecchi mostravano "Operazione X #uuid").
  const isAdmin =
    user?.ruolo_lega === "presidente_lega" ||
    user?.ruolo_lega === "admin" ||
    !!user?.ruoli_aggiuntivi?.some((r) => r === "presidente_lega" || r === "admin");
  const ricostruisciMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ aggiornati: number; messaggio: string }>(
        "/admin/budget/ricostruisci-descrizioni-mercato",
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["budget"] });
      alert(tr("✓ {{v1}}", { v1: data.messaggio }));
    },
    onError: (e) => {
      const err = e as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      alert(tr("Errore: {{v1}}", { v1: err.response?.data?.detail ?? err.message ?? "errore" }));
    },
  });

  return (
    <MobileShell logoSuffix="BUDGET">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Budget")}</h2>
        {stagioneAttiva && (
          <div className="text-sm text-fg-muted">
            Stagione <span className="text-fg font-bold">{stagioneAttiva.anno}</span>
          </div>
        )}
      </div>

      {!stagioneAttiva && (
        <div className="card text-fg-muted text-center p-4">{tr("Nessuna stagione attiva.")}</div>
      )}

      <div className="card">
        <SquadraSelector value={squadraId} onChange={setSquadraId} />
      </div>

      {squadraId && isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {budget && (
        <>
          {/* Saldo */}
          <div className="card">
            {budget.privacy_visibility && budget.privacy_visibility !== "visibile" && (
              <div
                className={`mb-3 p-2 rounded-lg border text-xs ${
                  budget.privacy_visibility === "nascosto"
                    ? "border-danger/40 bg-danger/10 text-danger"
                    : "border-warning/40 bg-warning/10 text-warning"
                }`}
              >
                {budget.privacy_visibility === "nascosto"
                  ? tr("🔒 Budget privato — non visibile")
                  : tr("🔐 Solo saldo aggregato — dettaglio movimenti nascosto")}
              </div>
            )}

            <div className="text-xs text-fg-muted uppercase tracking-wider">
              {tr("Saldo budget fantamilioni")}
            </div>
            <div className="font-display text-5xl text-accent mt-1">
              {budget.saldo_milioni === null ? "—" : `${Number(budget.saldo_milioni).toFixed(2)}M`}
            </div>
            <div className="text-xs text-fg-muted mt-1">
              {budget.num_movimenti} movimenti totali
            </div>
          </div>

          {/* Storico movimenti */}
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-2 bg-primary-deep/40 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider text-fg-muted">
                {tr("Storico movimenti budget")}
              </span>
              <div className="flex rounded-lg overflow-hidden border border-primary-soft/40 text-[10px] uppercase tracking-wider">
                <button
                  type="button"
                  onClick={() => setVista("gruppi")}
                  className={`px-2 py-1 ${vista === "gruppi" ? "bg-accent text-ink font-bold" : "text-fg-muted hover:text-fg"}`}
                >
                  {tr("Per tipo")}
                </button>
                <button
                  type="button"
                  onClick={() => setVista("cronologia")}
                  className={`px-2 py-1 ${vista === "cronologia" ? "bg-accent text-ink font-bold" : "text-fg-muted hover:text-fg"}`}
                >
                  {tr("Cronologia")}
                </button>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  disabled={ricostruisciMut.isPending}
                  onClick={() => {
                    if (
                      !confirm(
                        tr(
                          "Ricostruire le descrizioni dei movimenti delle operazioni di mercato?\n\n",
                        ) +
                          tr("Effetto:\n") +
                          tr("• Sostituisce 'Operazione acquisto #<uuid>' con\n") +
                          tr("  'Acquisto Tonali · con Squadra X' (formato leggibile)\n") +
                          tr("• Tocca solo i movimenti vecchi (quelli nuovi sono già corretti)\n") +
                          tr("• Operazione idempotente: puoi rilanciarla senza danni"),
                      )
                    )
                      return;
                    ricostruisciMut.mutate();
                  }}
                  className="text-[10px] px-2 py-1 rounded border border-info/40 text-info hover:bg-info/10 disabled:opacity-50"
                  title={tr("Admin: riscrive le descrizioni storiche con nomi giocatori/squadre")}
                >
                  {ricostruisciMut.isPending
                    ? tr("Ricostruzione…")
                    : tr("🔧 Ricostruisci descrizioni mercato")}
                </button>
              )}
            </div>
            {budget.ultimi_movimenti.length === 0 && (
              <div className="p-4 text-fg-muted text-sm italic">
                {budget.privacy_visibility === "aggregato"
                  ? tr("Dettaglio movimenti non visibile (privacy).")
                  : tr("Nessun movimento.")}
              </div>
            )}
            {vista === "cronologia" &&
              budget.ultimi_movimenti.map((m) => <RigaMovimento key={m.id} m={m} />)}
            {vista === "gruppi" &&
              gruppi.map((g) => (
                <div key={g.id} className="border-b border-primary/10 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => toggle(g.id)}
                    aria-expanded={!chiusi.has(g.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-primary-deep/25 hover:bg-primary-deep/40 text-left"
                  >
                    <span
                      className={`text-xs text-fg-muted transition-transform ${chiusi.has(g.id) ? "-rotate-90" : ""}`}
                      aria-hidden
                    >
                      ▾
                    </span>
                    <span className="font-bold text-sm text-fg flex-1 min-w-0 truncate">
                      {g.titolo}
                    </span>
                    <span className="text-[10px] text-fg-muted font-mono">
                      {tr("{{v1}} mov.", { v1: g.righe.length })}
                    </span>
                    <Importo valore={g.totale} className="text-sm" />
                  </button>
                  {!chiusi.has(g.id) && g.righe.map((m) => <RigaMovimento key={m.id} m={m} />)}
                </div>
              ))}
          </div>
        </>
      )}
    </MobileShell>
  );
}
